import { useState } from 'react'
import { UsersTableParams } from '..'
import { useUsers } from '../context/users-context'
import { UsersActionDialog } from './users-action-dialog'
import { UsersNewPassDialog } from './users-new-pass'
import { UsersResetPassDialog } from './users-reset-pass-dialog'
import { remove_user, update_user } from '@/api/user' // Import update_user
import { DeleteDialog } from '@/components/delete-dialog'
import { toast } from '@/hooks/use-toast'
import { useTranslation } from 'react-i18next'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { handleServerError } from '@/utils/handle-server-error' // Import error handler

interface UsersDialogsProps {
  params: UsersTableParams
  setParams: React.Dispatch<React.SetStateAction<UsersTableParams>>
}

export function UsersDialogs(props : UsersDialogsProps) {
  const i18n = useTranslation()
  const { open, setOpen, currentRow, setCurrentRow } = useUsers()
  const [pass, setPass] = useState("")
  const [resetOpen, setResetOpen] = useState(false)
  const [loading, setLoading] = useState(false) // Add loading state

  const onResetPassword = (passw: string) => {
    setPass(passw)
    setResetOpen(true)
  }

  const handleMakeAdmin = async (grant: boolean) => {
    if (!currentRow) return
    setLoading(true)
    try {
      const updatePayload = {
        is_admin: grant
      }
      await update_user(currentRow.id, updatePayload)
      toast({
        title: i18n.t(
          grant ? 'user_made_admin' : 'user_admin_revoked',
          { user: currentRow.name }
        ),
      })
      props.setParams({ ...props.params })
      setOpen(null)
      setCurrentRow(null)
    } catch (error) {
      handleServerError(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <UsersActionDialog
        key='user-add'
        open={open === 'add'}
        onOpenChange={() => setOpen('add')}
        setParams={props.setParams}
        params={props.params}
      />

      {currentRow && (
        <>
          <UsersNewPassDialog
            pass={pass}
            open={resetOpen}
            onOpenChange={setResetOpen}
            currentRow={currentRow}
          />

          <UsersResetPassDialog
            key={`user-rest-pass-${currentRow.id}`}
            open={open === 'reset_pass'}
            onOpenChange={() => {
              setOpen('reset_pass')
            }}
            currentRow={currentRow}
            onConfirm={onResetPassword}
          />

          <UsersActionDialog
            key={`user-edit-${currentRow.id}`}
            open={open === 'edit'}
            onOpenChange={() => {
              setOpen('edit')
              setTimeout(() => {
                setCurrentRow(null)
              }, 500)
            }}
            currentRow={currentRow}
            setParams={props.setParams}
            params={props.params}
          />

          <ConfirmDialog
            key={`user-make-admin-${currentRow.id}`}
            open={open === 'make_admin'}
            isLoading={loading}
            onOpenChange={() => {
              setOpen('make_admin')
              setTimeout(() => {
                setCurrentRow(null)
              }, 500)
            }}
            handleConfirm={() => handleMakeAdmin(true)}
            title={i18n.t('make_admin_confirm_title')}
            desc={i18n.t('make_admin_confirm_desc', { user: currentRow.name })}
            cancelBtnText={i18n.t('cancel')}
            confirmText={i18n.t('confirm')}
          />

          <ConfirmDialog
            key={`user-revoke-admin-${currentRow.id}`}
            open={open === 'revoke_admin'}
            isLoading={loading}
            onOpenChange={() => {
              setOpen('revoke_admin')
              setTimeout(() => {
                setCurrentRow(null)
              }, 500)
            }}
            handleConfirm={() => handleMakeAdmin(false)}
            title={i18n.t('revoke_admin_confirm_title')}
            desc={i18n.t('revoke_admin_confirm_desc', { user: currentRow.name })}
            cancelBtnText={i18n.t('cancel')}
            confirmText={i18n.t('confirm')}
          />

          <DeleteDialog
            key={`user-delete-${currentRow.id}`}
            open={open === 'delete'}
            onOpenChange={() => {
              setOpen('delete')
              setTimeout(() => {
                setCurrentRow(null)
              }, 500)
            }}
            onDeleted={() => {
              toast({
                title: i18n.t('user_removed', { user: currentRow.name }),
              })
              props.setParams({ ...props.params })
            }}
            getDeleteName={() => currentRow.name}
            deleteRow={() => remove_user(currentRow.id)}
            deleteTitle={i18n.t('delete_user')}
          />
        </>
      )}
    </>
  )
}
